import React from "react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  RefreshCw,
  RotateCcw,
  Search,
  X,
} from "lucide-react";
import PredictFilterRow from "./PredictFilterRow";
import PredictNavFoot from "./PredictNavFoot";
import { BP_RAIL_ESCAPE_LINKS } from "../../utils/bondPredictNavFooter";
import {
  CATEGORY_FILTERS,
  SORT_OPTIONS,
  STATUS_FILTERS,
  TYPE_FILTERS,
} from "../../utils/bondPredictHelpers";
import {
  BP_CATEGORY_ICONS,
  BP_LEAGUE_ALL_ICON,
  BP_STATUS_ICONS,
  BP_TYPE_ICONS,
} from "../../utils/sidebarNavIcons";

const PredictFiltersRail = ({
  status,
  type,
  category,
  league,
  leagues = [],
  sortKey,
  search = "",
  onSearchChange,
  onStatusChange,
  onTypeChange,
  onCategoryChange,
  onLeagueChange,
  onSortChange,
  onClear,
  onRefresh,
  refreshing,
  isLoading,
  activeCount,
  resultCount = 0,
  totalCount,
  liveCount = 0,
  compact,
  collapsed,
  onToggleCollapse,
  onClose,
}) => {
  const iconOnly = collapsed && !compact;
  const hasLeagues = leagues.length > 0;
  const total = totalCount ?? resultCount;

  return (
    <aside
      className={`floor-index bp-predict-floor-rail${iconOnly ? " bp-predict-floor-rail-collapsed" : ""}${
        compact ? " bp-predict-floor-rail-compact" : ""
      }`}
      aria-label="Market filters"
    >
      <div className={`floor-index-head${iconOnly ? " floor-index-head-icon-only" : ""}`}>
        {iconOnly ? (
          <button
            type="button"
            className="floor-index-refresh floor-index-collapse-btn"
            onClick={onToggleCollapse}
            aria-label="Expand filters"
            title="Expand"
          >
            <ChevronRight size={12} />
          </button>
        ) : (
          <>
            <div className="floor-index-brand">
              <span className="floor-index-brand-label">Markets desk</span>
              <span className="floor-index-brand-sub">Filter · sort · trade</span>
              {!compact && onToggleCollapse ? (
                <button
                  type="button"
                  className="floor-index-refresh floor-index-collapse-btn"
                  onClick={onToggleCollapse}
                  aria-label="Collapse filters"
                  title="Collapse"
                >
                  <ChevronLeft size={12} />
                </button>
              ) : null}
              {compact && onClose ? (
                <button
                  type="button"
                  className="floor-index-refresh floor-index-collapse-btn"
                  onClick={onClose}
                  aria-label="Close filters"
                >
                  <X size={12} />
                </button>
              ) : null}
            </div>

            {onSearchChange ? (
              <div className="floor-index-search">
                <Search size={13} className="floor-index-search-ico" aria-hidden />
                <input
                  type="search"
                  placeholder="Filter markets…"
                  value={search}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="floor-index-search-input"
                  aria-label="Search markets"
                />
                {search ? (
                  <button
                    type="button"
                    onClick={() => onSearchChange("")}
                    className="floor-index-search-clear"
                    aria-label="Clear search"
                  >
                    <X size={11} />
                  </button>
                ) : null}
              </div>
            ) : null}

            <div className="floor-index-chips" role="tablist" aria-label="Filter by status">
              {STATUS_FILTERS.map((f) => {
                const active = status === f.value;
                const count = f.value === "OPEN" && liveCount > 0 ? liveCount : undefined;
                return (
                  <button
                    type="button"
                    key={f.value || "all-status"}
                    role="tab"
                    aria-selected={active}
                    onClick={() => onStatusChange(f.value)}
                    className={`floor-index-chip${active ? " floor-index-chip-active" : ""}`}
                    title={f.hint}
                  >
                    <span>{f.label}</span>
                    {count != null ? <span className="floor-index-chip-count">{count}</span> : null}
                  </button>
                );
              })}
            </div>

            {onSortChange && onRefresh ? (
              <div className="floor-index-toolbar">
                <label className="floor-index-sort">
                  <span className="floor-index-sort-label">Sort</span>
                  <span className="floor-index-sort-wrap">
                    <select
                      value={sortKey}
                      onChange={(e) => onSortChange(e.target.value)}
                      className="floor-index-sort-select"
                      aria-label="Sort markets"
                    >
                      {SORT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={11} className="floor-index-sort-chev" aria-hidden />
                  </span>
                </label>
                <button
                  type="button"
                  onClick={onRefresh}
                  disabled={isLoading || refreshing}
                  className="floor-index-refresh"
                  aria-label="Refresh markets"
                  title="Refresh"
                >
                  {refreshing ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <RefreshCw size={12} />
                  )}
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>

      {!iconOnly ? (
        <div className="floor-index-summary" aria-live="polite">
          <span>
            <strong className="tabular-nums">{resultCount}</strong>
            {resultCount === total
              ? ` market${resultCount === 1 ? "" : "s"}`
              : ` / ${total}`}
            {liveCount > 0 ? (
              <>
                {" · "}
                <strong className="tabular-nums">{liveCount}</strong> live
              </>
            ) : null}
          </span>
        </div>
      ) : null}

      <div className="floor-index-list">
        {iconOnly
          ? STATUS_FILTERS.map((f) => (
              <PredictFilterRow
                key={f.value || "all-status-icon"}
                label={f.label}
                hint={f.hint}
                active={status === f.value}
                onClick={() => onStatusChange(f.value)}
                icon={BP_STATUS_ICONS[f.value ?? ""]}
                urgent={f.value === "OPEN"}
              />
            ))
          : null}

        {!iconOnly ? <p className="floor-index-sort-label floor-index-list-label">Type</p> : null}
        {TYPE_FILTERS.map((f, idx) => (
          <PredictFilterRow
            key={f.value || "all-types"}
            rank={iconOnly ? undefined : idx + 1}
            label={f.label}
            hint={f.hint}
            active={type === f.value}
            onClick={() => onTypeChange(f.value)}
            icon={BP_TYPE_ICONS[f.value ?? ""]}
          />
        ))}

        {!iconOnly ? <p className="floor-index-sort-label floor-index-list-label">Category</p> : null}
        {CATEGORY_FILTERS.map((f, idx) => (
          <PredictFilterRow
            key={f.value || "all-cat"}
            rank={iconOnly ? undefined : idx + 1}
            label={f.label}
            hint={f.hint}
            active={category === f.value}
            onClick={() => onCategoryChange(f.value)}
            icon={BP_CATEGORY_ICONS[f.value ?? ""]}
          />
        ))}

        {hasLeagues ? (
          <>
            {!iconOnly ? <p className="floor-index-sort-label floor-index-list-label">League</p> : null}
            <PredictFilterRow
              rank={iconOnly ? undefined : 1}
              label="All leagues"
              hint={`${leagues.reduce((sum, lg) => sum + (Number(lg.marketCount) || Number(lg.count) || 0), 0)} markets`}
              active={!league}
              onClick={() => onLeagueChange("")}
              icon={BP_LEAGUE_ALL_ICON}
            />
            {leagues.map((lg, idx) => {
              const count = Number(lg.marketCount) || Number(lg.count) || 0;
              return (
                <PredictFilterRow
                  key={lg.id}
                  rank={iconOnly ? undefined : idx + 2}
                  label={lg.name}
                  hint={`${count} market${count === 1 ? "" : "s"}`}
                  active={league === lg.id}
                  onClick={() => onLeagueChange(lg.id)}
                  logo={lg.logo}
                />
              );
            })}
          </>
        ) : null}

        {activeCount > 0 ? (
          <button type="button" className="floor-index-empty-reset" onClick={onClear}>
            {iconOnly ? (
              <RotateCcw size={11} />
            ) : (
              <>Clear {activeCount} filter{activeCount === 1 ? "" : "s"}</>
            )}
          </button>
        ) : null}
      </div>

      <PredictNavFoot links={BP_RAIL_ESCAPE_LINKS} iconOnly={iconOnly} />
    </aside>
  );
};

export default PredictFiltersRail;
